import * as ko from "knockout";
import { EnumTypeViewModel } from "./enum";
import { FileViewModel } from "./file";
import { UserViewModel } from "./user";
import {CustomError, customFetch as fetch} from '../fetch';
import {closePopups} from "../init";
import {formatToDateTime} from "../utils/dateHelper";
import {formatToCurrency} from "../utils/numberHelper";
import { IFilteringModel, IOrderingModel, ISearchStringModel, FilterColumn, OrderColumn } from '../utils/pagingHelper';

export enum SearchDeclarationFilterBy {
    Status = "Status",
}

export enum SearchDeclarationOrderBy {
    Title = "Title",
    Amount = "Amount",
    Status = "Status",
    CreatedAt = "CreatedAt",
    ReviewedAt = "ReviewedAt",
    EmployeeName = "EmployeeName",
    ReviewerName = "ReviewerName"
}

export interface SearchDeclarationModel extends
    ISearchStringModel,
    IOrderingModel<SearchDeclarationOrderBy>,
    IFilteringModel<SearchDeclarationFilterBy>
{
    id?: string;
    userId?: string;
    pageNumber?: number;
    pageSize?: number;
    ignorePaging?: boolean;
    searchStrings: Record<string, string>;
    orderBy?: OrderColumn<SearchDeclarationOrderBy>[];
    filterBy: FilterColumn<SearchDeclarationFilterBy>[];
}

export enum DeclarationStatus {
    Pending = 0,
    Approved = 1,
    Rejected = 2,
}

export interface DeclarationViewModel {
    id: string;
    title: string;
    description: string;
    amount: number;
    attachmentFile: FileViewModel | null;
    status: EnumTypeViewModel<DeclarationStatus>;
    rejectionReason: string | null;
    createdAt: Date;
    updatedAt: Date | null;
    reviewedAt: Date | null;
    user: UserViewModel;
    reviewedByAdmin: UserViewModel | null;
}

export type AdminDeclarationAction = 'view' | 'approve' | 'reject';
export class AdminDeclarationActionPopupModel {
    parent: any;
    action: AdminDeclarationAction;

    loading: KnockoutObservable<boolean>;
    errors: KnockoutObservableArray<CustomError>;

    declaration: KnockoutObservable<DeclarationViewModel>;
    reason: KnockoutObservable<string>;

    constructor(parent: any, action: AdminDeclarationAction, declaration?: DeclarationViewModel) {
        this.parent = parent;
        this.action = action;

        this.loading = ko.observable(false);
        this.errors = ko.observableArray<CustomError>();

        this.declaration = ko.observable(declaration);
        this.reason = ko.observable<string>()
            .extend({
                required: {
                    params: true,
                    message: 'Reden van afkeuring is verplicht',
                    onlyIf: () => action === 'reject'
                },
                errorHandler: { source: this.errors, id: 'Reason' }
            });
    }

    private getRequestConfig = () => {
        const isReject = this.action === 'reject';
        return {
            endpoint: isReject ? '/Admin/Declaration/Reject' : `/Admin/Declaration/Approve/${this.declaration()!.id}`,
            body: isReject ? { id: this.declaration()!.id, reason: this.reason() } : null
        };
    }

    async submit() {
        if (this.action === 'view') {
            return;
        }

        const validation = ko.validation.group(this);

        if (validation().length > 0) {
            validation.showAllMessages();
            return;
        }

        try {
            this.loading(true);
            const { endpoint, body } = this.getRequestConfig();

            const response = await fetch(endpoint, {
                method: 'PUT',
                body: body ? JSON.stringify(body) : undefined
            });

            if (response.ok) {
                this.parent.listView.load();
                this.cancel();
            } else if (response.errors != null && response.errors?.length) {
                this.errors(response.errors);
            }
        } finally {
            this.loading(false);
        }
    }

    cancel = () => closePopups();

    formatToDateTime = formatToDateTime;
    formatToCurrency = formatToCurrency;
}
